import useGetOrderById from '@hooks/useGetOrderById'
import Spinner from 'parkdepot-shared/components/Spinner'
import React from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { SpinnerContainer, Time, Container } from './PaymentFinished.style'
import { getFragmentData } from 'parkdepot-shared/gql'
import B2C_GET_OPEN_ORDERS, { OrderResponseFragment } from '@graphql/queries/getOpenOrders.query'
import { DateTime } from 'luxon'
import { ADYEN_SUPPORTED_RESULT_CODES, SupportedResultCodes } from './types'
import { useAuth } from '@context/AuthContext/AuthContext'
import Error from '@components/Error/Error.component'
import { useApolloClient } from '@apollo/client'
import { SvLotType } from 'parkdepot-shared/gql/graphql'
import { AdyenResultStatus } from '@customTypes/adyenResultStatus.enum'
import PaymentSuccessAnonymous from './components/PaymentSuccessAnonymous/PaymentSuccessAnonymous.component'
import PaymentPending from './components/PaymentPending/PaymentPending.component'
import PaymentSuccess from './components/PaymentSuccess/PaymentSuccess.component'
import useGetAdyenPaymentStatus from './useGetAdyenPaymentStatus.hook'
import PaymentError from './components/PaymentError/PaymentError.component'
import { CarpayOrderActionsStep } from '@components/CarpayOrderActions/CarpayOrderActions.component'
import { getRedirectUrl } from '@utils/getPaymentRedirectUrl.utils'
import { useSendGTMEvent } from '@hooks/useSendGTMEvent.hook'

const CarpayPaymentFinished = () => {
    const now = DateTime.now()
    const { order_id } = useParams<{ order_id: string }>()
    const { search } = useLocation()
    const { user } = useAuth()
    const client = useApolloClient()
    const searchParams = new URLSearchParams(search)
    const paymentStatus = searchParams.get('status') as SupportedResultCodes
    const [adyenResultCode, loadingAdyenStatus] = useGetAdyenPaymentStatus()
    const country = searchParams.get('country') || 'INT'
    const { sendCarpayPaymentSuccessEvent } = useSendGTMEvent()

    const isPaymentSuccessful = (resultCode: string | null | undefined | boolean) => {
        if (!resultCode || typeof resultCode === 'boolean') return false

        resultCode = resultCode.toString().toUpperCase()

        return (
            resultCode === AdyenResultStatus.AUTHORISED ||
            resultCode === AdyenResultStatus.PENDING ||
            resultCode === AdyenResultStatus.RECEIVED
        )
    }

    const response = useGetOrderById({
        skip: !order_id || !user || order_id === CarpayOrderActionsStep.ErrorFlow,
        fetchPolicy: 'cache-and-network',
        variables: { order_id }
    })

    let { data } = response
    const { loading, error, called } = response

    if ((loading && !data) || (loadingAdyenStatus && !adyenResultCode)) {
        return (
            <SpinnerContainer>
                <Spinner theme="primary" size="xl" />
            </SpinnerContainer>
        )
    }

    if (error || (!paymentStatus && !adyenResultCode)) {
        return <Error />
    }

    if (isPaymentSuccessful(adyenResultCode) && !data?.b2c_get_order_by_id) {
        return (
            <Container>
                <PaymentSuccessAnonymous now={now} />
            </Container>
        )
    }

    if (!data?.b2c_get_order_by_id && !called) {
        const storedOrder = client.readFragment({
            id: order_id,
            fragment: OrderResponseFragment
        })

        if (!storedOrder) {
            return <Error />
        }

        data = { b2c_get_order_by_id: storedOrder }
    }

    const orderResponse = getFragmentData(OrderResponseFragment, data!.b2c_get_order_by_id)

    // Just read from cache to avoid extra network call (if user refreshes page this will be empty)
    const ordersToBePaid = client.readQuery({
        query: B2C_GET_OPEN_ORDERS,
        variables: {
            plates: !user ? [orderResponse.order.plate] : user.license_plates.map((lp) => lp.plate)
        }
    })

    const adyenStatus = paymentStatus || adyenResultCode
    const price = orderResponse.price.discounted_parking_price
    const isTollroad = orderResponse.lot.sv_lot_type === SvLotType.TollRoad
    const plate = orderResponse.order.plate
    const priceValidUntil = orderResponse.price.price_valid_until
    const nextOrder = ordersToBePaid?.b2c_get_open_orders.orders
        .map((orderF) => getFragmentData(OrderResponseFragment, orderF))
        .find((order) => ['INCOMPLETE', 'UNPAID'].indexOf(order.order.status) > -1 && order.order.order_id !== order_id)

    const redirectUrl = getRedirectUrl(user, plate, country)
    if (isPaymentSuccessful(adyenStatus)) {
        sendCarpayPaymentSuccessEvent()
    }
    // TODO: handle other statuses
    let view = null

    if (isPaymentSuccessful(adyenStatus)) {
        view = (
            <Container>
                <PaymentSuccess
                    price={price}
                    plate={plate}
                    now={now}
                    priceValidUntil={priceValidUntil}
                    isTollroad={isTollroad}
                    nextOrderId={nextOrder ? nextOrder.order.order_id : null}
                    redirectUrl={redirectUrl}
                />
            </Container>
        )
    }

    if (adyenStatus === ADYEN_SUPPORTED_RESULT_CODES.ERROR || adyenStatus === ADYEN_SUPPORTED_RESULT_CODES.REFUSED) {
        view = (
            <Container>
                <PaymentError paymentStatus={adyenStatus} plate={plate} now={now} />
            </Container>
        )
    }

    if (adyenStatus === ADYEN_SUPPORTED_RESULT_CODES.PENDING)
        view = (
            <Container>
                <PaymentPending plate={plate} now={now} />
            </Container>
        )

    return view
}

export default CarpayPaymentFinished
